/**
 * CinematicJourney.tsx
 * React-Port der Vanilla-v1 (index.html / css/style.css / js/main.js).
 * Scroll-Scrub über <canvas>: Manifest laden -> Frames progressiv nachladen ->
 * Cover-Draw -> Lerp auf den Ziel-Frame -> Overlays nach Phasen-Fenstern.
 *
 * Bewusst imperativ (Refs + classList) statt State: die Schleife läuft mit
 * ~60 fps, ein setState pro Frame würde React durchgehend neu rendern.
 *
 * Frames + Manifest liegen unter public/journey/ (siehe manifest.json).
 */
import { useEffect, useRef, type CSSProperties } from "react";
import { Link } from "react-router-dom";
import Lenis from "lenis";
import { useLanguage } from "@/contexts/LanguageContext";
import "@/components/ai-label.css";
import "./journey.css";

/* --- Vertrag ------------------------------------------------------------ */
interface Markers {
  pull: number;
  digitize: number;
  dive: number;
  reachy: number;
  landing: number;
}

interface Variant {
  minWidth: number; // benötigte Zielbreite in Gerätepixeln (CSS-Breite × DPR)
  pattern: string;
  width: number;
  height: number;
}

interface Manifest {
  count: number;
  fps: number;
  width: number;
  height: number;
  pattern: string;
  markers: Markers;
  lum?: number[];
  /** Optionale Auflösungsstufen; die kleinste passende wird gewählt (Datenvolumen). */
  variants?: Variant[];
}

/** Wählt die kleinste Variante, deren Breite die Zielbreite (CSS × DPR, max 2) abdeckt. */
function pickVariant(variants: Variant[] | undefined, fallback: string): string {
  if (!variants || !variants.length) return fallback;
  // Zielbreite = CSS-Breite × DPR, aber DPR nur zu 60 % gewichtet: die Frames werden
  // per Canvas cover-skaliert, ein 1280er-Set auf einem 2x-Laptop ist visuell nicht
  // von 1920 zu unterscheiden, kostet aber 13 MB weniger (Nico 04.09.: langsames Netz).
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const need = (window.innerWidth || 1280) * (1 + (dpr - 1) * 0.6);
  const sorted = [...variants].sort((a, b) => a.width - b.width);
  for (const v of sorted) if (v.width * 1.05 >= need) return v.pattern;
  return sorted[sorted.length - 1].pattern;
}

interface PhaseWindow {
  key: string;
  from: number;
  to: number;
  el: HTMLElement | null;
}

/** Diagnose-Hook fuer Selbsttest/Debug (window.__egJourney). */
interface JourneyDebug {
  progress: () => number;
  hasFrames: () => boolean;
  frameIndex: () => number;
  activeOverlay: () => string;
  markers: () => Markers;
  scrollToProgress: (p: number) => void;
  mode: () => string;
  /** false = Zeichnen pausiert (Track ausserhalb des Viewports oder Tab im Hintergrund). */
  rendering: () => boolean;
}

declare global {
  interface Window {
    __egJourney?: JourneyDebug;
  }
}

const MANIFEST_URL = "/journey/manifest.json";

/** Nur Fallback, wenn /journey/manifest.json fehlt oder kaputt ist. */
const FALLBACK: Manifest = {
  count: 0,
  fps: 12,
  width: 1280,
  height: 720,
  pattern: "/assets/journey-v3/frame_%04d.webp",
  markers: { pull: 0, digitize: 0.3, dive: 0.4, reachy: 0.6, landing: 0.8 },
};

const HERO_END = 0.12;
const FIRST_BATCH = 8;      // sofort: die ersten Frames, damit der Hero steht
const KEY_STRIDE = 12;      // Stufe 2: jeder 12. Frame (= 1 pro Sekunde) über den ganzen Film
const CONCURRENCY = 8;
const NEAR_AHEAD = 24;      // Scroll-Priorität: Frames vor der aktuellen Position zuerst
const LERP = 0.12;

const clamp = (v: number, lo: number, hi: number) => (v < lo ? lo : v > hi ? hi : v);

/** "/journey/frame_%04d.webp" + 7 -> "/journey/frame_0007.webp" */
function framePath(pattern: string, oneBased: number): string {
  return pattern.replace(/%(\d+)d/, (_m, width: string) =>
    String(oneBased).padStart(parseInt(width, 10), "0"),
  );
}

const CinematicJourney = () => {
  const { t } = useLanguage();

  const rootRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const cueRef = useRef<HTMLButtonElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fillRef = useRef<HTMLSpanElement>(null);
  const readoutRef = useRef<HTMLSpanElement>(null);
  const stepperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = rootRef.current;
    const track = trackRef.current;
    const stage = stageRef.current;
    const canvas = canvasRef.current;
    if (!root || !track || !stage || !canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const reduceMotion =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    /* --- Zustand --------------------------------------------------------- */
    let cancelled = false;
    let manifest: Manifest | null = null; // null = Fallback (Verlauf statt Frames)
    let markers: Markers = FALLBACK.markers;
    let frameCount = 0;
    let frames: HTMLImageElement[] = [];
    let lastImg: HTMLImageElement | null = null;
    let targetIndex = 0;
    let currentIndex = 0;
    let lastDrawnIndex = -1;
    let lastFallbackKey = -1;
    let trackTop = 0;
    let trackSpan = 1;
    let progress = 0;
    let windows: PhaseWindow[] = [];
    let activeKey = "";
    let stepperOn = -1;
    let rafId = 0;
    let lenis: Lenis | null = null;

    // Render-Pause: solange der Track ausserhalb des Viewports liegt oder der Tab
    // im Hintergrund ist, wird nicht gezeichnet. Lenis laeuft trotzdem weiter,
    // sonst waere der Smooth-Scroll der ganzen Seite tot.
    let trackVisible = true; // IntersectionObserver meldet sich erst asynchron
    let forceSnap = false;   // beim Aufwachen nicht erst 30 Frames lang hinlerpen
    const isRendering = () => trackVisible && !document.hidden;

    /* --- Messen ---------------------------------------------------------- */
    function measure() {
      const rect = track.getBoundingClientRect();
      trackTop = rect.top + window.scrollY;
      trackSpan = Math.max(track.offsetHeight - window.innerHeight, 1);
    }

    const readProgress = () => clamp((window.scrollY - trackTop) / trackSpan, 0, 1);
    const progressToScrollY = (p: number) => trackTop + clamp(p, 0, 1) * trackSpan;

    /* --- Canvas ---------------------------------------------------------- */
    function sizeCanvas() {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = stage.clientWidth || window.innerWidth;
      const h = stage.clientHeight || window.innerHeight;
      canvas.width = Math.max(1, Math.round(w * dpr));
      canvas.height = Math.max(1, Math.round(h * dpr));
      lastDrawnIndex = -1; // Redraw-Guard invalidieren
      lastFallbackKey = -1;
    }

    function drawCover(img: HTMLImageElement): boolean {
      const cw = canvas.width;
      const ch = canvas.height;
      const iw = img.naturalWidth || img.width;
      const ih = img.naturalHeight || img.height;
      if (!iw || !ih) return false;
      const scale = Math.max(cw / iw, ch / ih);
      const dw = iw * scale;
      const dh = ih * scale;
      ctx.drawImage(img, (cw - dw) / 2, (ch - dh) / 2, dw, dh);
      return true;
    }

    /** Kein Manifest / keine Frames: dunkler Verlauf + HUD-Raster, kein Bruch. */
    function drawFallback(p: number) {
      const cw = canvas.width;
      const ch = canvas.height;
      const grad = ctx.createLinearGradient(0, 0, 0, ch);
      grad.addColorStop(0, "#1B4332");
      grad.addColorStop(1, "#0F2A1F");
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, cw, ch);

      ctx.lineWidth = 1;
      ctx.strokeStyle = "rgba(255,255,255,0.05)";
      const stepX = Math.max(56, Math.round(cw / 18));
      const stepY = Math.max(56, Math.round(ch / 10));
      ctx.beginPath();
      for (let x = stepX; x < cw; x += stepX) {
        ctx.moveTo(x + 0.5, 0);
        ctx.lineTo(x + 0.5, ch);
      }
      for (let y = stepY; y < ch; y += stepY) {
        ctx.moveTo(0, y + 0.5);
        ctx.lineTo(cw, y + 0.5);
      }
      ctx.stroke();

      const sy = Math.round(clamp(p, 0, 1) * (ch - 2)) + 0.5;
      ctx.strokeStyle = "rgba(92,163,54,0.42)";
      ctx.beginPath();
      ctx.moveTo(0, sy);
      ctx.lineTo(cw, sy);
      ctx.stroke();
    }

    function paint(p: number) {
      if (!manifest) {
        const key = Math.round(clamp(p, 0, 1) * 400);
        if (key !== lastFallbackKey) {
          lastFallbackKey = key;
          drawFallback(p);
        }
        return;
      }
      const idx = clamp(Math.round(currentIndex), 0, frameCount - 1);
      if (idx === lastDrawnIndex) return;
      let img = frames[idx];
      if (!img) {
        // nächstgelegenen geladenen Frame nehmen (erst zurück, dann vor), statt irgendeinen
        for (let d = 1; d < frameCount && !img; d++) {
          if (idx - d >= 0 && frames[idx - d]) img = frames[idx - d];
          else if (idx + d < frameCount && frames[idx + d]) img = frames[idx + d];
        }
        if (!img) img = lastImg;
      }
      if (!img) {
        if (lastFallbackKey === -1) {
          lastFallbackKey = 0;
          drawFallback(p);
        }
        return;
      }
      if (drawCover(img)) {
        lastImg = img;
        lastDrawnIndex = frames[idx] ? idx : -1; // Ersatzbild: beim nächsten Tick erneut prüfen
      }
    }

    /* --- Frame-Loader ---------------------------------------------------- */
    function loadFrame(i: number): Promise<boolean> {
      return new Promise((resolve) => {
        if (cancelled || !manifest) {
          resolve(false);
          return;
        }
        const img = new Image();
        img.decoding = "async";
        img.onload = () => {
          if (cancelled) {
            resolve(false);
            return;
          }
          // Erst in frames[] eintragen, wenn der Frame fertig dekodiert ist —
          // sonst dekodiert der erste drawImage() synchron im rAF und ruckelt.
          const commit = () => {
            if (cancelled) {
              resolve(false);
              return;
            }
            frames[i] = img;
            if (!lastImg) {
              lastImg = img;
              lastDrawnIndex = -1;
            }
            if (i === clamp(Math.round(currentIndex), 0, frameCount - 1)) {
              lastDrawnIndex = -1; // sofort nachzeichnen
            }
            resolve(true);
          };
          // decode() fehlt in manchen Browsern oder wirft — dann wie bisher direkt setzen.
          if (typeof img.decode === "function") img.decode().then(commit).catch(commit);
          else commit();
        };
        img.onerror = () => resolve(false);
        img.src = framePath(manifest.pattern, i + 1);
      });
    }

    function runQueue(indices: number[], concurrency: number, done?: () => void) {
      let next = 0;
      let active = 0;
      let finished = 0;
      const total = indices.length;
      if (!total) {
        done?.();
        return;
      }
      const onOne = () => {
        active--;
        finished++;
        if (cancelled) return;
        if (finished >= total) {
          done?.();
          return;
        }
        pump();
      };
      function pump() {
        while (active < concurrency && next < total && !cancelled) {
          active++;
          loadFrame(indices[next++]).then(onOne);
        }
      }
      pump();
    }

    /* --- Laden in drei Stufen, immer scroll-priorisiert --------------------
       1. FIRST_BATCH: die ersten Frames, damit der Hero sofort steht.
       2. Keyframes: jeder KEY_STRIDE-te Frame über den ganzen Film. Danach gibt es
          an jeder Scrollposition ein Bild in maximal 1 s Abstand, kein Standbild mehr.
       3. Rest: Lücken auffüllen. Die Warteschlange wird bei jedem Abruf neu nach
          Abstand zur aktuellen Scrollposition sortiert (nach vorn gewichtet). */
    const requested = new Set<number>();
    const pending: number[] = [];
    let activeLoads = 0;

    function priorityOf(i: number): number {
      const cur = Math.round(currentIndex);
      const d = i - cur;
      // vor der Position zählt einfach, dahinter doppelt (man scrollt meist vorwärts)
      return d >= 0 ? (d <= NEAR_AHEAD ? d : NEAR_AHEAD + (d - NEAR_AHEAD) * 1.5) : -d * 2;
    }

    function pump() {
      while (activeLoads < CONCURRENCY && pending.length && !cancelled) {
        // billig genug: 403 Einträge, nur bei freiem Slot
        let best = 0;
        let bestP = priorityOf(pending[0]);
        for (let k = 1; k < pending.length; k++) {
          const pk = priorityOf(pending[k]);
          if (pk < bestP) { bestP = pk; best = k; }
        }
        const i = pending.splice(best, 1)[0];
        activeLoads++;
        loadFrame(i).then(() => {
          activeLoads--;
          if (!cancelled) pump();
        });
      }
    }

    function enqueue(indices: number[]) {
      for (const i of indices) {
        if (!requested.has(i)) { requested.add(i); pending.push(i); }
      }
      pump();
    }

    function startLoading() {
      const head: number[] = [];
      for (let i = 0; i < Math.min(FIRST_BATCH, frameCount); i++) head.push(i);
      const keys: number[] = [];
      for (let i = KEY_STRIDE; i < frameCount; i += KEY_STRIDE) keys.push(i);
      keys.push(frameCount - 1);
      const rest: number[] = [];
      for (let i = 0; i < frameCount; i++) rest.push(i);
      // Reihenfolge der Queue ist egal, priorityOf entscheidet; head/keys zuerst
      // einreihen sorgt nur für den ersten Pump-Durchlauf.
      enqueue(head);
      enqueue(keys);
      enqueue(rest);
    }

    /* --- Phasen-Fenster aus den Markern ---------------------------------- */
    function buildWindows() {
      const dig = markers.digitize;
      const dive = markers.dive;
      const rea = markers.reachy;
      const land = markers.landing;
      // Schutz gegen degenerierte Marker: liegt digitize NICHT hinter HERO_END,
      // bekäme die Problem-Phase kein Fenster. Nur dann rückt das Hero-Ende vor;
      // bei gültigen Markern bleibt es exakt bei HERO_END.
      const heroEnd = dig > HERO_END ? HERO_END : dig * 0.6;
      const third = Math.max((dig - heroEnd) / 3, 0.001);

      windows = [
        { key: "hero", from: 0, to: heroEnd, el: null },
        { key: "p1", from: heroEnd, to: heroEnd + third, el: null },
        { key: "p2", from: heroEnd + third, to: heroEnd + third * 2, el: null },
        { key: "p3", from: heroEnd + third * 2, to: dig, el: null },
        { key: "s1", from: dig, to: dive, el: null },
        { key: "s2", from: dive, to: rea, el: null },
        { key: "s3", from: rea, to: land, el: null },
        { key: "landing", from: land, to: 1.0001, el: null },
      ];
      windows.forEach((w) => {
        w.el = root.querySelector<HTMLElement>('[data-ov="' + w.key + '"]');
      });
    }

    /* --- Hell/Dunkel-Modus: hell bis zum Digitize-Marker ("01"), ab dort dunkel ---
       (Nico 03.09.: ab 01 weiße Schrift). Der Marker liegt per build-journey.sh auf dem
       ersten dunklen Frame, damit Weiß nie auf dem hellen Bild landet. */
    let mode = "";
    function updateMode() {
      const next = progress < markers.digitize ? "light" : "dark";
      if (next !== mode) {
        mode = next;
        root.classList.toggle("is-light", mode === "light");
        root.classList.toggle("is-dark", mode === "dark");
      }
    }

    function updateOverlays(p: number) {
      let found = "";
      for (let i = 0; i < windows.length; i++) {
        if (p >= windows[i].from && p < windows[i].to) {
          found = windows[i].key;
          break;
        }
      }
      if (!found) found = p >= markers.landing ? "landing" : "hero";

      if (found !== activeKey) {
        activeKey = found;
        windows.forEach((w) => {
          w.el?.classList.toggle("is-active", w.key === found);
        });
      }

      cueRef.current?.classList.toggle("is-hidden", p > 0.012);

      const stepperEl = stepperRef.current;
      const inSteps = p >= markers.digitize && p < markers.landing;
      stepperEl?.classList.toggle("is-active", inSteps);
      let on = -1;
      if (inSteps) on = found === "s1" ? 1 : found === "s2" ? 2 : 3;
      if (on !== stepperOn && stepperEl) {
        stepperOn = on;
        stepperEl.querySelectorAll<HTMLElement>(".eg-journey__dot").forEach((d) => {
          d.classList.toggle("is-on", parseInt(d.dataset.step || "0", 10) === on);
        });
      }
    }

    /* --- HUD ------------------------------------------------------------- */
    function updateHud(p: number) {
      if (fillRef.current) fillRef.current.style.height = (p * 100).toFixed(2) + "%";
      if (readoutRef.current) readoutRef.current.textContent = (p * 100).toFixed(1) + " %";
    }

    /* --- Render-Schleife -------------------------------------------------- */
    function render() {
      progress = readProgress();

      if (manifest) {
        targetIndex = progress * (frameCount - 1);
        if (reduceMotion || forceSnap) {
          // forceSnap: nach einer Render-Pause direkt auf den richtigen Frame,
          // statt ihn ueber ~30 Frames anzulerpen.
          currentIndex = targetIndex;
        } else {
          currentIndex += (targetIndex - currentIndex) * LERP;
          if (Math.abs(targetIndex - currentIndex) < 0.01) currentIndex = targetIndex;
        }
        forceSnap = false;
      }

      paint(progress);
      updateMode();
      updateOverlays(progress);
      updateHud(progress);
    }

    function frame(time: number) {
      if (cancelled) return;
      // Lenis laeuft IMMER — der Smooth-Scroll gehoert der ganzen Seite,
      // nicht nur dem Journey-Track. Nur das Zeichnen pausiert.
      lenis?.raf(time);
      if (isRendering()) render();
      rafId = requestAnimationFrame(frame);
    }

    /** Nach einer Pause: veralteten Frame verwerfen und einmal frisch zeichnen. */
    function resumeRender() {
      if (cancelled || !isRendering()) return;
      lastDrawnIndex = -1;
      lastFallbackKey = -1;
      forceSnap = true;
      render();
    }

    function scrollToProgress(p: number) {
      measure();
      const y = progressToScrollY(p);
      if (lenis) lenis.scrollTo(y, { immediate: true });
      else window.scrollTo(0, y);
    }

    // Klick auf die Scroll-Aufforderung: weich bis zur ersten Problem-Station
    const cueEl = cueRef.current;
    const onCueClick = () => {
      measure();
      const y = progressToScrollY(HERO_END + 0.03);
      if (lenis) lenis.scrollTo(y, { duration: 1.6 });
      else window.scrollTo({ top: y, behavior: "smooth" });
    };
    cueEl?.addEventListener("click", onCueClick);

    /* --- Manifest -------------------------------------------------------- */
    function applyManifest(data: Partial<Manifest>) {
      const count = parseInt(String(data.count), 10);
      const src = (data.markers || {}) as Partial<Markers>;
      const nextMarkers = {} as Markers;
      (Object.keys(FALLBACK.markers) as (keyof Markers)[]).forEach((k) => {
        const v = typeof src[k] === "number" ? (src[k] as number) : FALLBACK.markers[k];
        nextMarkers[k] = clamp(v, 0, 1);
      });
      manifest = {
        count: count > 0 ? count : FALLBACK.count,
        fps: data.fps || FALLBACK.fps,
        width: data.width || FALLBACK.width,
        height: data.height || FALLBACK.height,
        pattern: pickVariant(
          data.variants,
          typeof data.pattern === "string" ? data.pattern : FALLBACK.pattern,
        ),
        markers: nextMarkers,
        lum: Array.isArray(data.lum) ? data.lum : undefined,
        variants: data.variants,
      };
      markers = manifest.markers;
      frameCount = Math.max(manifest.count, 1);
      frames = new Array(frameCount);
    }

    /* --- Boot ------------------------------------------------------------ */
    measure();
    sizeCanvas();
    buildWindows();
    paint(0);

    if (!reduceMotion) {
      lenis = new Lenis({ smoothWheel: true, lerp: 0.08 });
    }

    const onResize = () => {
      measure();
      sizeCanvas();
      render();
    };
    window.addEventListener("resize", onResize, { passive: true });
    const onLoad = () => {
      measure();
      render();
    };
    window.addEventListener("load", onLoad);

    // Track ausserhalb des Viewports -> Zeichnen aussetzen. 200px Vorlauf,
    // damit beim Zurueckscrollen schon ein frischer Frame steht.
    let trackObserver: IntersectionObserver | null = null;
    if (typeof IntersectionObserver === "function") {
      trackObserver = new IntersectionObserver(
        (entries) => {
          if (cancelled) return;
          const wasVisible = trackVisible;
          trackVisible = entries.some((e) => e.isIntersecting);
          if (trackVisible && !wasVisible) resumeRender();
        },
        { rootMargin: "200px 0px" },
      );
      trackObserver.observe(track);
    }

    // Hintergrund-Tab: rAF laeuft ggf. gedrosselt weiter, zeichnen muss es nicht.
    const onVisibility = () => {
      if (cancelled) return;
      if (!document.hidden) resumeRender();
    };
    document.addEventListener("visibilitychange", onVisibility);

    // Diagnose-Hook (Selbsttest/Debug)
    window.__egJourney = {
      progress: () => progress,
      hasFrames: () => !!manifest,
      frameIndex: () => clamp(Math.round(currentIndex), 0, Math.max(frameCount - 1, 0)),
      activeOverlay: () => activeKey,
      markers: () => markers,
      scrollToProgress,
      mode: () => mode,
      rendering: isRendering,
    };

    rafId = requestAnimationFrame(frame);

    // Manifest laden — schlägt es fehl, bleibt der Fallback aktiv und es
    // wird kein einziger Frame angefragt.
    const abort = new AbortController();
    fetch(MANIFEST_URL, { cache: "no-cache", signal: abort.signal })
      .then((res) => {
        if (!res.ok) throw new Error("manifest " + res.status);
        return res.json();
      })
      .then((data) => {
        if (cancelled) return;
        applyManifest(data);
        buildWindows();
        activeKey = "";
        lastDrawnIndex = -1;
        startLoading();
      })
      .catch(() => {
        if (cancelled) return;
        manifest = null; // Fallback: Verlauf + HUD-Raster
        markers = FALLBACK.markers;
        buildWindows();
        activeKey = "";
        lastFallbackKey = -1;
        paint(progress);
      });

    return () => {
      cancelled = true;
      abort.abort();
      cancelAnimationFrame(rafId);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("load", onLoad);
      trackObserver?.disconnect();
      trackObserver = null;
      document.removeEventListener("visibilitychange", onVisibility);
      lenis?.destroy();
      lenis = null;
      frames = [];
      lastImg = null;
      cueEl?.removeEventListener("click", onCueClick);
      delete window.__egJourney;
    };
  }, []);

  const agents = ["net", "revenue", "state", "forecast", "service"];

  return (
    <div className="eg-journey" ref={rootRef}>
      <section className="eg-journey__track" ref={trackRef} aria-label="Energieglück Journey">
        <div className="eg-journey__stage" ref={stageRef}>
          <canvas className="eg-journey__canvas" ref={canvasRef} aria-hidden="true" />


          <svg className="eg-journey__grain" aria-hidden="true" focusable="false" preserveAspectRatio="none">
            <filter id="eg-journey-grain">
              <feTurbulence type="fractalNoise" baseFrequency="0.82" numOctaves={3} stitchTiles="stitch" />
              <feColorMatrix type="saturate" values="0" />
            </filter>
            <rect width="100%" height="100%" filter="url(#eg-journey-grain)" />
          </svg>

          <div className="eg-journey__hud" aria-hidden="true">
            <span className="eg-journey__line eg-journey__line--v eg-journey__line--v1" />
            <span className="eg-journey__line eg-journey__line--v eg-journey__line--v2" />
            <span className="eg-journey__line eg-journey__line--h eg-journey__line--h1" />
            <span className="eg-journey__line eg-journey__line--h eg-journey__line--h2" />
            <span className="eg-journey__corner eg-journey__corner--tl" />
            <span className="eg-journey__corner eg-journey__corner--tr" />
            <span className="eg-journey__corner eg-journey__corner--bl" />
            <span className="eg-journey__corner eg-journey__corner--br" />
            <span className="eg-journey__tag eg-journey__tag--br" ref={readoutRef}>
              00.0 %
            </span>
            <div className="eg-journey__rail">
              <span className="eg-journey__fill" ref={fillRef} />
            </div>
          </div>

          {/* KI-Kennzeichnung (Art. 50 Abs. 4 KI-VO) — bewusst ausserhalb des
              aria-hidden HUD, damit sie auch fuer Screenreader existiert.
              Bleibt waehrend des gesamten Films sichtbar. */}
          <span className="eg-ai-label eg-journey__tag eg-journey__tag--bl">
            {t("journey.aiLabel.short")}
          </span>

          <div className="eg-journey__overlays">
            {/* Hero */}
            <div className="eg-journey__ov eg-journey__ov--hero is-active" data-ov="hero">
              <h1 className="eg-journey__display eg-journey__display--claim">{t("journey.hero.headline")}</h1>
              {/* Scroll-Aufforderung (Variante D): Maus-Silhouette mit laufendem Rad, Touch: Chevrons nach oben */}
              <button type="button" className="eg-journey__cue" ref={cueRef} aria-label={t("journey.hero.scroll")}>
                <svg className="eg-journey__cue-mouse" viewBox="0 0 24 38" aria-hidden="true" focusable="false">
                  <rect x="1.5" y="1.5" width="21" height="35" rx="10.5" />
                  <circle className="eg-journey__cue-wheel" cx="12" cy="10" r="2.2" />
                </svg>
                <svg className="eg-journey__cue-chevrons" viewBox="0 0 20 24" aria-hidden="true" focusable="false">
                  <path d="M3 4l7 7 7-7" /><path d="M3 12l7 7 7-7" />
                </svg>
              </button>
            </div>

            {/* Problem */}
            {["p1", "p2", "p3"].map((key) => (
              <div className="eg-journey__ov eg-journey__ov--corner" data-ov={key} key={key}>
                <span className="eg-journey__eyebrow">{t("journey.problem.eyebrow")}</span>
                <h2 className="eg-journey__display eg-journey__display--line">
                  {t("journey.problem." + key)}
                </h2>
              </div>
            ))}

            {/* So funktioniert es */}
            {["s1", "s2", "s3"].map((key, i) => (
              <div className="eg-journey__ov eg-journey__ov--corner" data-ov={key} key={key}>
                <span className="eg-journey__eyebrow">{t("journey.how.eyebrow")}</span>
                <h2 className="eg-journey__display eg-journey__display--step">
                  <span className="eg-journey__num">{"0" + (i + 1)}</span>
                  {t("journey.how." + key + ".title")}
                </h2>
                <p className="eg-journey__sub">{t("journey.how." + key + ".text")}</p>
              </div>
            ))}

            <div className="eg-journey__stepper" ref={stepperRef} aria-hidden="true">
              <span className="eg-journey__dot" data-step="1" />
              <span className="eg-journey__dot" data-step="2" />
              <span className="eg-journey__dot" data-step="3" />
            </div>

            {/* Landing */}
            <div className="eg-journey__ov eg-journey__ov--landing" data-ov="landing">
              <span className="eg-journey__eyebrow">{t("journey.landing.eyebrow")}</span>
              <h2 className="eg-journey__display eg-journey__display--landing">
                {t("journey.landing.headline")}
              </h2>
              <div className="eg-journey__grid">
                {agents.map((slug, i) => (
                  <div
                    className="eg-journey__card"
                    key={slug}
                    style={{ "--eg-i": i } as CSSProperties}
                  >
                    <h3 className="eg-journey__card-title">
                      {t("journey.landing.agents." + slug + ".title")}
                    </h3>
                    <p className="eg-journey__card-text">
                      {t("journey.landing.agents." + slug + ".text")}
                    </p>
                  </div>
                ))}
                <div
                  className="eg-journey__card eg-journey__card--more"
                  style={{ "--eg-i": agents.length } as CSSProperties}
                >
                  <h3 className="eg-journey__card-title">{t("journey.landing.agents.more.title")}</h3>
                  <p className="eg-journey__card-text">{t("journey.landing.agents.more.text")}</p>
                  <Link className="eg-journey__card-link" to="/ki">
                    {t("journey.landing.agents.more.link")} →
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default CinematicJourney;
